import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {BOTTOM_BAR_DEFAULT_HEIGHT, BOTTOM_BAR_MAX_HEIGHT, BOTTOM_BAR_MIN_HEIGHT} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {MOBILE_TIMELINE_DEFAULT_HEIGHT, MOBILE_TIMELINE_MAX_HEIGHT, MOBILE_TIMELINE_MIN_HEIGHT} from '@Components/poster-editor/components/poster-editor-mobile-timeline/poster-editor-mobile-timeline.types';

// Intentionally not "pmw"-prefixed — PMW's per-release cache wipe (clearNamespaceFromCache('pmw')) deletes every "pmw*" key, which would reset this device preference on each deploy.
const BOTTOM_BAR_HEIGHT_STORAGE_KEY = 'editor_timeline_bottom_bar_height';
const MOBILE_TIMELINE_HEIGHT_STORAGE_KEY = 'editor_timeline_mobile_height';

const getPersistedHeight = (storageKey: string, bounds: {min: number; max: number; fallback: number}): number => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = Number(stored);
      if (!isNaN(parsed) && parsed >= bounds.min && parsed <= bounds.max) {
        return parsed;
      }
    }
  } catch {
    // localStorage may be unavailable
  }
  return bounds.fallback;
};

const persistHeight = (storageKey: string, height: number): void => {
  try {
    localStorage.setItem(storageKey, String(height));
  } catch {
    // localStorage may be unavailable
  }
};

export const persistBottomBarHeight = (height: number): void => {
  persistHeight(BOTTOM_BAR_HEIGHT_STORAGE_KEY, height);
};

export const persistMobileTimelineHeight = (height: number): void => {
  persistHeight(MOBILE_TIMELINE_HEIGHT_STORAGE_KEY, height);
};

interface State {
  bottomBarHeight?: number;
  mobileTimelineHeight?: number;
}

const initialState: State = {
  bottomBarHeight: getPersistedHeight(BOTTOM_BAR_HEIGHT_STORAGE_KEY, {min: BOTTOM_BAR_MIN_HEIGHT, max: BOTTOM_BAR_MAX_HEIGHT, fallback: BOTTOM_BAR_DEFAULT_HEIGHT}),
  mobileTimelineHeight: getPersistedHeight(MOBILE_TIMELINE_HEIGHT_STORAGE_KEY, {min: MOBILE_TIMELINE_MIN_HEIGHT, max: MOBILE_TIMELINE_MAX_HEIGHT, fallback: MOBILE_TIMELINE_DEFAULT_HEIGHT}),
};

export const posterEditorWebBottomBarSlice = createSlice({
  name: 'posterEditorWebBottomBar',
  initialState,
  reducers: {
    updateTimelineBottomBarHeight: (state, action: PayloadAction<number>): void => {
      state.bottomBarHeight = action.payload;
    },
    updateMobileTimelineHeight: (state, action: PayloadAction<number>): void => {
      state.mobileTimelineHeight = action.payload;
    },
  },
});

export const {updateTimelineBottomBarHeight, updateMobileTimelineHeight} = posterEditorWebBottomBarSlice.actions;

export const posterEditorWebBottomBarReducer = posterEditorWebBottomBarSlice.reducer;
